import { create } from 'zustand';
import type { Role, Theme, ModalState, Toast, ToastTone } from '../types';
import { showToast } from '../components/toastComponent';

const SESSION_KEY = 'sk_hospitality.session';

function parseJwtClaims(token: string): Record<string, unknown> {
  try {
    const [, b64] = token.split('.');
    return JSON.parse(atob(b64.replace(/-/g, '+').replace(/_/g, '/')));
  } catch { return {}; }
}

function loadSession() {
  if (typeof localStorage === 'undefined') return null;
  try {
    return JSON.parse(localStorage.getItem(SESSION_KEY) || 'null');
  } catch { return null; }
}

function saveSession(data: object) {
  if (typeof localStorage === 'undefined') return;
  try { localStorage.setItem(SESSION_KEY, JSON.stringify(data)); } catch {}
}

function clearSession() {
  if (typeof localStorage === 'undefined') return;
  try { localStorage.removeItem(SESSION_KEY); } catch {}
}

interface AppState {
  // Session
  role: Role;
  profileId: string;
  /** platform_org_id claim from the JWT — the hosp adapter's tenantId. */
  orgId: string;
  /** org_name claim from the JWT — the tenant's display name (e.g. "Softknack"). */
  orgName: string;
  /** The org's primary platform location id (identity.locations, loc_<ULID>) —
   *  hosp_location.id must BE this id; hosp mints no location id of its own.
   *  Fetched separately (not part of /v1/me) by hospitalitySync's bootstrap. */
  platformLocationId: string;
  firstName: string;
  displayName: string;
  email: string;
  theme: Theme;
  isAuthenticated: boolean;
  token: string | null;

  // UI state
  modal: ModalState;
  toasts: Toast[];

  // Actions
  login: (token: string, role: Role, profileId: string, firstName?: string, displayName?: string, email?: string, orgId?: string, orgName?: string) => void;
  logout: () => void;
  toggleTheme: () => void;
  openModal: (type: ModalState['type'], props?: Record<string, unknown>) => void;
  closeModal: () => void;
  /** Auto-dismisses at 3.4s, matching the prototype's own this.toast(). */
  toast: (message: string, tone?: ToastTone) => void;
  dismissToast: (id: string) => void;
}

const saved = loadSession();

export const useAppStore = create<AppState>((set, get) => ({
  role: saved?.role ?? 'operator',
  profileId: saved?.profileId ?? '',
  orgId: saved?.orgId ?? '',
  orgName: saved?.orgName ?? '',
  platformLocationId: '',
  firstName: saved?.firstName ?? '',
  displayName: saved?.displayName ?? '',
  email: saved?.email ?? '',
  theme: saved?.theme ?? 'light',
  isAuthenticated: !!saved?.hasSession,
  token: null,
  modal: { type: null },
  toasts: [],

  login: (token, role, profileId, firstName = '', displayName = '', email = '', orgId = '', orgName = '') => {
    // Prefer explicit org values (from /v1/me) — the cookie-session path passes the
    // sentinel 'session' token which has no claims. Fall back to JWT claims only for
    // the magic-link path where a real token is present.
    const claims = parseJwtClaims(token);
    const resolvedOrgId = orgId || (claims.platform_org_id as string) || '';
    const resolvedOrgName = orgName || (claims.org_name as string) || '';
    set({ token, role, profileId, orgId: resolvedOrgId, orgName: resolvedOrgName, firstName, displayName, email, isAuthenticated: true });
    saveSession({ hasSession: true, role, profileId, orgId: resolvedOrgId, orgName: resolvedOrgName, firstName, displayName, email, theme: get().theme });
  },

  logout: () => {
    clearSession();
    set({ token: null, isAuthenticated: false, role: 'operator', profileId: '', orgName: '', platformLocationId: '', firstName: '', displayName: '', email: '' });
  },

  toggleTheme: () => {
    const next = get().theme === 'light' ? 'dark' : 'light';
    set({ theme: next });
    saveSession({ hasSession: true, role: get().role, profileId: get().profileId, orgId: get().orgId, firstName: get().firstName, displayName: get().displayName, email: get().email, theme: next });
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('data-theme', next);
    }
  },

  openModal: (type, props) => set({ modal: { type, props } }),
  closeModal: () => set({ modal: { type: null } }),

  // Delegates to the shared react-native-toast-message host (see components/toast*).
  // tone → toast type: danger/warn → error, info → info, else success.
  toast: (message, tone = 'success') => {
    const type = tone === 'danger' || tone === 'warn' ? 'error' : tone === 'info' ? 'info' : 'success';
    showToast(type, message);
  },

  // Retained for interface compatibility; the toast host now auto-hides each toast.
  dismissToast: () => set({ toasts: [] }),
}));
